import { useEffect, useState } from 'react'
import { useShallow } from 'zustand/react/shallow'

import { useDocumentState } from '@/state'
import { useDocumentPageQuery } from '@/pages/DocumentPage/queries'
import { listTemplateAttributesQuery } from '@/services/queries/template_attributes'
import { usePDFDownloadEnabled } from '@/components/collaborating/PDFDownloadButton/hooks/usePDFDownloadEnabled'

export const useCheckDocumentImagesRendered = () => {
  const [nImages, setNImages] = useState<number | null>(null)
  const [isRendered, setIsRendered] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const enabled = usePDFDownloadEnabled()
  const { isLoading, data } = useDocumentPageQuery()
  const documentPages = data?.documentPages

  const nImagesLoaded = useDocumentState(
    useShallow((state) => state.nImagesLoaded),
  )
  const resetImagesLoaded = useDocumentState(
    useShallow((state) => state.resetImagesLoaded),
  )

  useEffect(() => {
    if (documentPages) {
      resetImagesLoaded()
    }
  }, [documentPages, resetImagesLoaded])

  useEffect(() => {
    if (isLoading || !documentPages || !enabled) return
    let nImagesInDocument = 0

    const promises = documentPages.map((page) =>
      listTemplateAttributesQuery({ documentPageId: page.id || '' })
        .then((attributes) => {
          attributes.forEach((attr) => {
            if (attr.data_type === 'image' || attr.data_type === 'view') {
              nImagesInDocument++
            }
          })
        })
        .catch((e) => {
          console.error('Error loading template attributes:', e)
          setError(e)
        }),
    )

    Promise.all(promises)
      .then(() => {
        setNImages(nImagesInDocument)
      })
      .catch((e) => {
        console.error('Error loading template attributes:', e)
        setError(e)
      })
  }, [isLoading, enabled, documentPages])

  useEffect(() => {
    if (nImages === null) return
    if (nImagesLoaded >= nImages) {
      setIsRendered(true)
    }
  }, [nImages, nImagesLoaded])

  if (error) {
    console.warn('Error checking document images:', error)
    return { isRendered: true }
  }

  return { isRendered }
}
